<script setup>
  import { computed, onMounted, onUnmounted, getCurrentInstance, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Loader2 } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
  import { Label } from "@/components/ui/label";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import ProfileUrlParams from "@/components/Survey/Profile/ProfileUrlParams.vue";
  import ProfileQuestionParams from "@/components/Survey/Profile/ProfileQuestionParams.vue";
  import { useProfileEditStore } from "@stores/survey/profiles/profileEdit";
  import { useProfileListStore } from "@stores/survey/profiles/profileList";
  import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useCardsStore } from "@stores/survey/cards";

  const props = defineProps({ outcome: { type: Object, default: null } });
  const emit = defineEmits(["close"]);

  const profileEditStore = useProfileEditStore();
  const { currentProfile, countryOptions, currentProfileErrors, isLoagingGenerateDescription } =
    storeToRefs(profileEditStore);
  const { nameIsUsed, updateProfileCountry, updateProfileLanguage, generateDescription, adsChannelHandlers } =
    profileEditStore;
  const surveyProfilesStore = useSurveyProfilesStore();
  const { selectedProfileIndex } = storeToRefs(surveyProfilesStore);
  const { duplicateProfile } = useProfileListStore();

  const { localeOptions } = storeToRefs(useSurveyLocalesStore());
  const { token: surveyToken, isAiChatSurvey } = storeToRefs(useSurveyStore());
  const { cards } = storeToRefs(useCardsStore());

  const saving = ref(false);

  const hasAiChatCard = computed(() => cards.value.some((card) => card.type === "ai_chat_v2"));

  const languageSelectOptions = computed(() =>
    localeOptions.value.map((item) => ({ label: item.label, value: item.language })),
  );

  const maxRespondents = computed(() => (isAiChatSurvey.value ? 3 : 150));
  const isAi = computed(() => currentProfile.value.token?.startsWith("ai-"));
  const lastRun = computed(() =>
    currentProfile.value.launched_at
      ? new Date(currentProfile.value.launched_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })
      : null,
  );
  const origin = computed(() =>
    [isAi.value && "Created by Episto AI", lastRun.value && `last run ${lastRun.value}`].filter(Boolean).join(" · "),
  );

  const validationErrors = computed(() => {
    const name = currentProfile.value?.name?.trim?.() ?? "";

    return {
      name: name === "" ? "Can't be empty" : nameIsUsed(name) ? "Name is already used" : "",
      n_target:
        currentProfile.value?.n_target >= 1 && currentProfile.value?.n_target <= maxRespondents.value
          ? ""
          : `Between 1 and ${maxRespondents.value}`,
      country: currentProfile.value?.country ? "" : "Can't be empty",
      language: isAiChatSurvey.value && !currentProfile.value?.language ? "Can't be empty" : "",
    };
  });
  const invalid = computed(() => Object.values(validationErrors.value).some(Boolean));

  const share = (count) => `${props.outcome?.respondents ? (count / props.outcome.respondents) * 100 : 0}%`;

  const save = async (run = false) => {
    saving.value = true;
    await surveyProfilesStore.saveProfile(selectedProfileIndex.value);
    if (run) {
      await surveyProfilesStore.launch(selectedProfileIndex.value);
      await surveyProfilesStore.refresh(surveyToken.value);
    }
    saving.value = false;
    if (run) emit("close");
  };

  const remove = async () => {
    await surveyProfilesStore.deleteProfile(selectedProfileIndex.value);
    emit("close");
  };

  const instance = getCurrentInstance();
  const cable = instance.proxy.$cable;

  onMounted(() => {
    cable.subscribe({ channel: "ProfilesDescriptionChannel", survey_token: surveyToken.value });
    cable._addChannel("ProfilesDescriptionChannel", adsChannelHandlers(cable, surveyToken.value), {
      _uid: instance.uid,
      _value: instance.proxy,
    });
  });

  onUnmounted(() => {
    cable.unsubscribe("ProfilesDescriptionChannel");
  });
</script>

<template>
  <SheetHeader class="border-b pr-12">
    <SheetTitle class="flex items-center gap-2">
      <AiStar
        v-if="isAi"
        class="size-4 shrink-0 text-status-info-strong" />
      <Input
        v-model="currentProfile.name"
        aria-label="Profile name"
        :aria-invalid="Boolean(validationErrors.name)"
        class="heading-title h-8 border-transparent px-1 shadow-none hover:border-border" />
    </SheetTitle>
    <SheetDescription v-if="origin">{{ origin }}</SheetDescription>
    <p
      v-if="validationErrors.name"
      class="text-xs text-destructive">
      {{ validationErrors.name }}
    </p>
  </SheetHeader>

  <div class="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
    <Badge
      v-for="error in currentProfileErrors"
      :key="`error-${error.message}`"
      variant="destructive">
      {{ error.message }}
    </Badge>

    <div
      v-if="outcome"
      class="flex flex-col gap-2 rounded-lg bg-muted p-3">
      <span class="text-xs text-muted-foreground">Last run</span>
      <div class="flex h-1.5 overflow-hidden rounded-full bg-background">
        <span
          class="bg-status-success"
          :style="{ width: share(outcome.completes) }" />
        <span
          class="bg-status-error"
          :style="{ width: share(outcome.screened_out) }" />
        <span
          class="bg-status-warning"
          :style="{ width: share(outcome.stopped) }" />
      </div>
      <span class="text-foreground-secondary">
        {{ outcome.completes }} completed · {{ outcome.screened_out }} screened out · {{ outcome.stopped }} stopped
      </span>
    </div>

    <div class="grid grid-cols-2 gap-3">
      <div class="space-y-2">
        <Label>Respondents</Label>
        <Input
          v-model.number="currentProfile.n_target"
          type="number"
          :min="1"
          :max="maxRespondents"
          :aria-invalid="Boolean(validationErrors.n_target)" />
        <p
          v-if="validationErrors.n_target"
          class="text-xs text-destructive">
          {{ validationErrors.n_target }}
        </p>
      </div>
      <div class="space-y-2">
        <Label>Country</Label>
        <Select
          :model-value="currentProfile.country"
          :options="countryOptions"
          filterable
          placeholder="Select"
          @update:model-value="updateProfileCountry" />
        <p
          v-if="validationErrors.country"
          class="text-xs text-destructive">
          {{ validationErrors.country }}
        </p>
      </div>
    </div>

    <template v-if="isAiChatSurvey">
      <div class="space-y-2">
        <Label>Language</Label>
        <Select
          :model-value="currentProfile.language"
          :options="languageSelectOptions"
          filterable
          placeholder="Select"
          @update:model-value="updateProfileLanguage" />
        <p
          v-if="validationErrors.language"
          class="text-xs text-destructive">
          {{ validationErrors.language }}
        </p>
      </div>
    </template>

    <div
      v-if="hasAiChatCard"
      class="space-y-2">
      <div class="flex items-center justify-between">
        <Label>Persona</Label>
        <Button
          variant="outline"
          size="sm"
          :disabled="isLoagingGenerateDescription"
          @click="generateDescription">
          <Loader2
            v-if="isLoagingGenerateDescription"
            class="size-4 animate-spin" />
          <AiStar
            v-else
            class="size-4" />
          Generate
        </Button>
      </div>
      <Textarea
        v-model="currentProfile.description"
        :rows="3"
        placeholder="Who they are, how they answer. Generated by Episto AI at launch when empty" />
    </div>

    <ProfileQuestionParams />
    <ProfileUrlParams />
  </div>

  <SheetFooter class="flex-row border-t">
    <Button
      variant="ghost"
      class="mr-auto text-destructive"
      @click="remove">
      Delete profile
    </Button>
    <Button
      variant="outline"
      @click="duplicateProfile(selectedProfileIndex)">
      Duplicate
    </Button>
    <Button
      variant="outline"
      :disabled="saving || invalid"
      @click="save()">
      Save
    </Button>
    <Button
      :disabled="saving || invalid"
      @click="save(true)">
      <Loader2
        v-if="saving"
        class="size-4 animate-spin" />
      Save and run
    </Button>
  </SheetFooter>
</template>
